import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

// Map title codes to role names
const titleCodeToRole = {
  'SYS_ADMIN': 'System Admin',
  'HR_MANAGER': 'HR Manager',
  'ASSESSMENT_MANAGER': 'Assessment Manager',
  'ASSESSOR': 'Assessor',
  'DEPT_HEAD': 'Department Head',
  'SENIOR_STAFF': 'Senior Staff',
  'STAFF': 'Staff',
  'INTERN': 'Intern'
};

// Helper function to map title code to role
const mapTitleCodeToRole = (titleCode) => {
  return titleCodeToRole[titleCode] || 'User';
};

// Create axios instance - baseURL will be set dynamically via interceptor
const api = axios.create({
  baseURL: '/api', // Default, will be overridden by interceptor if needed
  headers: {
    'Content-Type': 'application/json',
  },
});

// In-flight/short-TTL cache for /auth/me to eliminate duplicate fan-out
// when many components call getCurrentUser() on mount.
const _AUTH_ME_TTL_MS = 10_000;
const _authMeCache = { inflight: null, value: null, at: 0 };
function _invalidateAuthMeCache() {
  _authMeCache.inflight = null;
  _authMeCache.value = null;
  _authMeCache.at = 0;
}

// Other services (e.g. profileService.updateProfile) need to drop the
// /auth/me cache after they mutate the user record so the next read does
// not return the pre-edit snapshot.
export function invalidateAuthMeCache() {
  _invalidateAuthMeCache();
}

// Interceptor to set base URL and token dynamically at request time
api.interceptors.request.use((config) => {
  // Get the correct API base URL at request time (runtime, not build time)
  const apiBaseUrl = getApiBaseUrl();
  
  // If the URL is absolute (starts with http), use it directly
  if (apiBaseUrl.startsWith('http')) {
    config.baseURL = apiBaseUrl;
  } else {
    // If it's a relative path (like '/api'), use it as is
    config.baseURL = apiBaseUrl;
  }
  
  // Add token to requests if available and valid
  const token = localStorage.getItem('token');
  if (token && typeof token === 'string' && token.trim() !== '') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

// Response interceptor: surface friendly messages for gateway/server errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 502 || error.response?.status === 503) {
      error.userMessage = 'Service temporarily unavailable. Please try again in a moment.';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      error.userMessage = 'Unable to reach the server. Check your connection and try again.';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async signUp(name, email, password, department = '', title = '') {
    try {
      const response = await api.post('/auth/signup', {
        name,
        email,
        password,
        department,
        title,
      });
      
      const { user, token } = response.data;
      
      // Ensure first_name and last_name are populated
      const nameParts = name ? name.split(' ') : [];
      const firstName = user.first_name || nameParts[0] || '';
      const lastName = user.last_name || nameParts.slice(1).join(' ') || '';
      
      // Map title code to role
      const userWithRole = {
        ...user,
        first_name: firstName,
        last_name: lastName,
        name: user.name || name || `${firstName} ${lastName}`.trim() || 'User',
        role: user.role || mapTitleCodeToRole(user.title_code)
      };
      
      // Store token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userWithRole));
      _invalidateAuthMeCache();

      return { user: userWithRole, token };
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Network error. Please try again.');
    }
  },

  // ============================================
  // DEMO SIGNUP
  // Part of Phase 1: Demo System Foundation
  // Updated for Email Verification Before Login
  // ============================================
  async demoSignUp(name, email, password) {
    try {
      const response = await api.post('/auth/demo-signup', {
        name,
        email,
        password
      });
      
      const { user, token, requires_verification, redirect_url } = response.data;
      
      // If email verification is required, DO NOT store token
      if (requires_verification) {
        return {
          user: user || {},
          requires_verification: true,
          redirect_url: redirect_url || '/verify-email'
        };
      }
      
      // Legacy flow: if token provided (shouldn't happen for new signups)
      if (token) {
        // Ensure first_name and last_name are populated
        const nameParts = name ? name.split(' ') : [];
        const firstName = user.first_name || nameParts[0] || '';
        const lastName = user.last_name || nameParts.slice(1).join(' ') || '';
        
        const userWithRole = {
          ...user,
          first_name: firstName,
          last_name: lastName,
          name: user.name || name || `${firstName} ${lastName}`.trim() || 'User',
          role: user.role || 'User',
          account_type: user.account_type || 'demo',
          demo_expires_at: user.demo_expires_at,
          organization_id: user.organization_id
        };
        
        // Store token and user data
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userWithRole));
        _invalidateAuthMeCache();

        return { user: userWithRole, token };
      }

      return { user: user || {} };
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error_code === 'FREE_EMAIL_NOT_ALLOWED') {
        throw new Error('Please use your work email (not Gmail, Yahoo, Hotmail, etc.)');
      } else if (error.response?.data?.error_code === 'DOMAIN_LIMIT_EXCEEDED') {
        throw new Error('Your organization has reached the demo account limit. Please contact existing users.');
      }
      throw new Error('Network error. Please try again.');
    }
  },

  // ============================================
  // EMAIL VERIFICATION
  // Part of Email Verification Before Login Implementation
  // ============================================
  async verifyEmail(token) {
    try {
      const response = await api.get(`/auth/verify-email/${token}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to verify email. Please try again.');
    }
  },

  async resendVerification(email) {
    try {
      const response = await api.post('/auth/resend-verification', {
        email
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to resend verification email. Please try again.');
    }
  },

  async checkVerificationStatus(email) {
    try {
      const response = await api.get('/auth/verify-status', {
        params: { email }
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to check verification status. Please try again.');
    }
  },

  // Get demo status
  async getSelfAssessmentStatus() {
    try {
      const response = await api.get('/auth/self-assessment/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching self assessment status:', error);
      throw error;
    }
  },

  // Legacy method for backward compatibility
  async getDemoStatus() {
    try {
      const response = await api.get('/auth/demo/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching demo status:', error);
      throw error;
    }
  },

  async signIn(email, password) {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });
      
      const { user, token } = response.data;

      // Trust the canonical first_name / last_name returned by the backend
      // (sourced from first_name_en / last_name_en). Splitting user.name was
      // the original bug: when DB.name was empty the server returned the
      // literal string 'User', which then clobbered the real first name.
      const firstName = user.first_name || user.first_name_en || '';
      const lastName = user.last_name || user.last_name_en || '';

      const userWithRole = {
        ...user,
        first_name: firstName,
        last_name: lastName,
        name: user.name || `${firstName} ${lastName}`.trim() || user.email || '',
        role: user.role || mapTitleCodeToRole(user.title_code),
        // Preserve account_type if backend provides it
        account_type: user.account_type || null,
        organization_id: user.organization_id || null,
        demo_expires_at: user.demo_expires_at || null // Preserve demo expiration for fallback logic
      };
      
      // Store token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userWithRole));
      _invalidateAuthMeCache();

      return { user: userWithRole, token };
    } catch (error) {
      if (error.response?.status === 502 || error.response?.status === 503) {
        throw new Error(error.userMessage || 'Service temporarily unavailable. Please try again in a moment.');
      }
      if (error.code === 'ERR_NETWORK' || !error.response) {
        throw new Error(error.userMessage || 'Unable to reach the server. Check your connection and try again.');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.userMessage || 'Network error. Please try again.');
    }
  },

  async signOut() {
    try {
      await api.post('/auth/signout');
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      // Clear local storage regardless of API response
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      _invalidateAuthMeCache();
    }
  },

  async getCurrentUser() {
    // Dedupe: if a /auth/me request is already in-flight, reuse it. If one completed
    // within the TTL window, return the cached value. Many components call this on
    // mount; without dedup we see 6–8x requests per page load.
    if (_authMeCache.inflight) return _authMeCache.inflight;
    if (_authMeCache.value && (Date.now() - _authMeCache.at) < _AUTH_ME_TTL_MS) {
      return _authMeCache.value;
    }

    _authMeCache.inflight = (async () => {
      try {
        const response = await api.get('/auth/me');
        const user = response.data.user || response.data;

        const cachedUser = this.getUser();

        // The backend now returns canonical first_name / last_name (sourced
        // from first_name_en / last_name_en). Trust those directly — splitting
        // user.name was the root cause of saved profile values being clobbered
        // back to "User" on every refresh.
        const firstName = user.first_name || user.first_name_en || '';
        const lastName = user.last_name || user.last_name_en || '';

        let accountType = null;
        if ('account_type' in user) {
          accountType = user.account_type;
          if (accountType === null || accountType === undefined) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn('Backend returned null/undefined account_type - account may be converted');
            }
          }
        } else {
          accountType = cachedUser?.account_type || null;
        }

        const userWithRole = {
          ...user,
          first_name: firstName,
          last_name: lastName,
          name: user.name || `${firstName} ${lastName}`.trim() || user.email || '',
          role: user.role || mapTitleCodeToRole(user.title_code),
          account_type: accountType,
          organization_id: user.organization_id || cachedUser?.organization_id || null,
          demo_expires_at: user.demo_expires_at || cachedUser?.demo_expires_at || null
        };

        _authMeCache.value = userWithRole;
        _authMeCache.at = Date.now();
        return userWithRole;
      } catch (error) {
        console.error('Error getting current user:', error);
        if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 404) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          _invalidateAuthMeCache();
          throw new Error('Session expired - Please log in again');
        }
        throw new Error('Failed to get user data');
      } finally {
        _authMeCache.inflight = null;
      }
    })();

    return _authMeCache.inflight;
  },

  isAuthenticated() {
    const token = localStorage.getItem('token');
    if (!token || typeof token !== 'string' || token.trim() === '') {
      return false;
    }
    
    try {
      // Validate JWT format (should have 3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length !== 3) {
        // Token is not a valid JWT format
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return false;
      }
      
      // Check if token is expired by decoding it
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      
      if (!payload.exp || payload.exp <= now) {
        // Token is expired
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return false;
      }
      
      return true;
    } catch (error) {
      // If token is malformed or cannot be decoded, clear it
      console.error('Token validation error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return false;
    }
  },

  getToken() {
    const token = localStorage.getItem('token');
    // Return token only if it's valid
    if (token && typeof token === 'string' && token.trim() !== '') {
      return token;
    }
    return null;
  },

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
};
